import { Directive, ElementRef, HostListener, Input, Renderer2 } from '@angular/core';

@Directive({
  selector: '[keyboardNavigable]'
})
export class KeyboardNavigableDirective {
  @Input() keyboardNavigable!: string; // CSS class for navigable items
  private parentIndex = 0; // Tracks the current parent container
  private childIndex = 0; // Tracks the current child within the parent

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.setDefaultFocus();
    
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    
    const parentContainers = this.el.nativeElement.querySelectorAll(`.navigable-container:has(.${this.keyboardNavigable})`); // Parent containers
    if (!parentContainers || parentContainers.length === 0) return;

    const currentParent = parentContainers[this.parentIndex];
    const childItems = currentParent.querySelectorAll(`.${this.keyboardNavigable}`); // Children in the current parent
    if (!childItems || childItems.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigateParents(parentContainers, 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.navigateParents(parentContainers, -1);
        break;

      case 'ArrowRight':
        event.preventDefault();
        this.navigateChildren(childItems, 1);
        break;

      case 'ArrowLeft':
        event.preventDefault();
        this.navigateChildren(childItems, -1);
        break;

      case 'Tab': // Tab key navigation
        event.preventDefault();
        this.navigateWithTab(parentContainers,childItems, event.shiftKey ? -1 : 1); // Shift+Tab navigates backwards
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        this.triggerClick(childItems[this.childIndex]);
        break;
    }
  }

  private navigateParents(parentContainers: NodeListOf<HTMLElement>, step: number): void {
    const newIndex = this.parentIndex + step;

    // Prevent navigation beyond the start or end of parent containers
    if (newIndex < 0 || newIndex >= parentContainers.length) return;

    // Remove focus from the current child
    const currentParent = parentContainers[this.parentIndex];
    const currentChildItems = currentParent.querySelectorAll(`.${this.keyboardNavigable}`);
    if (currentChildItems.length > 0) {
      this.removeFocus(currentChildItems[this.childIndex] as HTMLElement);
    }

    // Update parent index and wait for DOM changes (if caused by *ngIf)
    this.parentIndex = newIndex;
    setTimeout(() => {
      const nextParent = parentContainers[this.parentIndex];
      const nextChildItems = nextParent.querySelectorAll(`.${this.keyboardNavigable}`);
      if (nextChildItems.length > 0) {
        this.childIndex = 0; // Always select the first child
        this.focusItem(nextChildItems[this.childIndex] as HTMLElement);
      }
    });
  }


  private navigateChildren(items: NodeListOf<HTMLElement>, step: number): void {
    const newIndex = this.childIndex + step;

    // Prevent navigation beyond the start or end of child items
    if (newIndex < 0 || newIndex >= items.length) return;

    // Remove focus from the current child
    this.removeFocus(items[this.childIndex]);

    // Update child index and apply focus
    this.childIndex = newIndex;
    this.focusItem(items[this.childIndex]);
  }

  private navigateWithTab(
    parentContainers: NodeListOf<HTMLElement>,
    childItems: NodeListOf<HTMLElement>,
    step: number
  ): void {
    if (
      (step === 1 && this.childIndex === childItems.length - 1) || // Last child
      (step === -1 && this.childIndex === 0) // First child
    ) {
      // Move to the next/previous parent if applicable
      const newParentIndex = this.parentIndex + step;
      if (newParentIndex >= 0 && newParentIndex < parentContainers.length) {
        this.parentIndex = newParentIndex;
        const nextParent = parentContainers[this.parentIndex];
        const nextChildItems = nextParent.querySelectorAll(`.${this.keyboardNavigable}`);
        if (nextChildItems.length > 0) {
          this.childIndex = step === 1 ? 0 : nextChildItems.length - 1; // Go to first/last child
          this.focusItem(nextChildItems[this.childIndex] as HTMLElement);
        }
      }
    } else {
      // Move within the same parent
      this.navigateChildren(childItems, step);
    }
  }

  private focusItem(item: HTMLElement): void {
    this.renderer.addClass(item, 'focused');
    item.focus();

    // Scroll the item into the horizontal center of its parent
    item.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center', // Ensures horizontal centering
    });
  }

  private removeFocus(item: HTMLElement): void {
    this.renderer.removeClass(item, 'focused');
  }

  private triggerClick(item: HTMLElement): void {
    item.click();
  }

  private setDefaultFocus(): void {
    
    const parentContainers = this.el.nativeElement.querySelectorAll('.offer-containers'); // Parent containers
    if (!parentContainers || parentContainers.length === 0) return;

    const firstParent = parentContainers[0];
    const firstChildItems = firstParent.querySelectorAll(`.${this.keyboardNavigable}`);
    if (!firstChildItems || firstChildItems.length === 0) return;

    this.focusItem(firstChildItems[0]); // Focus on the first child
  }
}
